import { type FC, memo } from "react";

import type { DocumentMetadata } from "@moment/api-collab/api-types";
import { findPageBySlugPath } from "@moment/state";

import { Head, type OpenGraphProps } from "~/components/seo/Head";
import { DESCRIPTION_MAX_LENGTH, formatTitle } from "~/components/seo/utils";
import { type StoredPage } from "~/state/types/editors.types";
import { DEFAULT_CANVAS_TITLE, DEFAULT_PAGE_TITLE } from "~/strings";
import { truncate } from "~/utils/format/string";
import { createCanvasUrl } from "~/utils/helpers/url";

export const documentOpenGraphProps = (
	canvas?: DocumentMetadata,
	pageData?: StoredPage,
	pageSlug?: string
): OpenGraphProps => {
	const props: OpenGraphProps = { title: null, description: null, url: null };
	const pageMetadata = canvas
		? (findPageBySlugPath(canvas.pagesMetadata, pageSlug ?? "") ??
			(pageSlug ? canvas.pagesMetadata.find((page) => page.slug === pageSlug) : undefined))
		: undefined;
	const pageTitle = pageMetadata?.title || DEFAULT_PAGE_TITLE;
	const canvasTitle = canvas?.title || DEFAULT_CANVAS_TITLE;

	if (canvas === undefined) {
		return props;
	}

	if (pageTitle !== undefined) {
		props.title = formatTitle([pageTitle, canvasTitle]);
	}

	// Cheap approximate parse of the first paragraph of the document. THIS COULD FAIL. If so we may
	// need to get serious and parse the document.
	props.description = truncate(
		pageData?.doc?.["content"]?.[0]?.["content"]?.[0]?.["text"] ?? "",
		DESCRIPTION_MAX_LENGTH
	);

	const pageSlugSuffix = pageSlug ? `/${pageSlug}` : "";

	const canvasUrl = createCanvasUrl({
		type: "CanvasById",
		workspaceName: "",
		id: canvas.id,
	});
	props.url = `${canvasUrl}${pageSlugSuffix}`;

	return props;
};

const _ServerSideRenderedDocumentHead: FC<{
	canvas?: DocumentMetadata;
	pageData?: StoredPage;
	pageSlug?: string;
}> = ({ canvas, pageData, pageSlug }) => {
	const expensiveProps = documentOpenGraphProps(canvas, pageData, pageSlug);
	return <Head {...expensiveProps} />;
};
_ServerSideRenderedDocumentHead.displayName = "SeoDocumentHead";

/**
 * StaticDocumentHead is a memoized component that renders the document head for the published
 * document.
 */
export const StaticDocumentHead = memo(_ServerSideRenderedDocumentHead);
